import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Dimmer,
  Grid,
  GridColumn,
  Header,
  HeaderContent,
  Icon,
  Loader,
  Message,
} from "semantic-ui-react";
import PaymentMethodSelector from "./PaymentMethodSelector";
import "./css/monnet.css";
import PaymentInfo from "./PaymentInfo";
import { getPaymentInfo, getPaymentStatus } from "./api/monnet";
import { SubStatusEnum } from "../../model/subStatusEnum";
import { StatusEnum } from "../../model/statusEnum";
import { useAuth } from "../shared/AuthContext/AuthContext";

const MonnetVoucherHome = () => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [experienceVoucher, setExperienceVoucher] = React.useState(null);
  const [errorMessage, setErrorMessage] = React.useState(null);
  const [isSuccess, setIsSuccess] = React.useState(false);
  const [redirectUrl, setRedirectUrl] = React.useState(null);
  const [isRedirecting, setIsRedirecting] = React.useState(false);

  const { tenant, publicKey } = useParams();
  const { getAuthToken } = useAuth();

  const desactiveIsLogin = () => {
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };

  const getPaymentStatusData = async () => {
    const accessToken = await getAuthToken();

    getPaymentStatus(accessToken, tenant, publicKey)
      .then((response) => {
        const paymentStatusData = response.data;
        if (paymentStatusData.urlRedirect && paymentStatusData.urlRedirect.trim()) {
          setRedirectUrl(paymentStatusData.urlRedirect);
        } else if (paymentStatusData.statusMessage === "ERROR") {
          switch (paymentStatusData.subStatusCode) {
            case 1:
            case 2:
              setErrorMessage("Payment link has expired");
              break;
            case 3:
              setErrorMessage("Payment rejected");
              break;
            case 4:
            case 5:
              setErrorMessage("Payment refunded");
              break;
            default:
              setErrorMessage("Payment processed with error");
          }
          setIsLoading(false);
        } else {
          getPaymentMethods(accessToken);
        }
      })
      .catch((error) => {
        console.log(error);
        setErrorMessage("An error ocurred while getting payment information.");
        setIsLoading(false);
      });
  };

  const getPaymentMethods = async (accessToken) => {
    try {
      const response = await getPaymentInfo(accessToken, tenant, publicKey);
      const responseData = await response.data;
      setExperienceVoucher(responseData);
    } catch (error) {
      // Verificamos si el error es 404
      if (error.response && error.response.status === 404) {
        try {
          const statusResponse = await getPaymentStatus(accessToken, tenant, publicKey);
          const paymentStatusData = statusResponse.data;

          if (paymentStatusData.statusCode === StatusEnum.SUCCESS) {
            setIsSuccess(true);
            setErrorMessage("¡El pago ha sido procesado exitosamente!");
          } else if (
            paymentStatusData.statusCode === StatusEnum.ERROR &&
            paymentStatusData.subStatusCode === SubStatusEnum.EXPIRED
          ) {
            setErrorMessage("El enlace de pago ha expirado");
          } else {
            setErrorMessage("El pago no existe. Verifique los datos e intente nuevamente.");
          }
        } catch (statusError) {
          setErrorMessage("Ocurrió un error al verificar el estado del pago.");
        }
      } else {
        console.error("Error en getPaymentMethods:", error);
        setErrorMessage("Ocurrió un error al obtener la información del pago.");
      }
    } finally {
      desactiveIsLogin();
    }
  };

  useEffect(() => {
    if (experienceVoucher === null) {
      setIsLoading(true);
      getPaymentStatusData();
    }
  }, []);

  useEffect(() => {
    if (redirectUrl) {
      setIsRedirecting(true);
      const timer = setTimeout(() => {
        window.location.href = redirectUrl;
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [redirectUrl]);

  if (isLoading || isRedirecting) {
    return (
      <Dimmer active>
        <Loader size="massive">{isRedirecting ? "Redirecting to external URL." : "Loading"}</Loader>
      </Dimmer>
    );
  }

  return (
    <div>
      {isLoading && (
        <Dimmer active>
          <Loader size="massive">Loading</Loader>
        </Dimmer>
      )}

      {errorMessage ? (
        <>
          <div className="not-found">
            <Message floating>
              <Header as="h1" icon textAlign="center" style={{ fontSize: "xx-large" }}>
                <Icon
                  name={isSuccess ? "check circle outline" : "warning circle"}
                  style={{ color: isSuccess ? "#008000" : "#cc0101" }}
                />
                <HeaderContent style={{ color: isSuccess ? "#008000" : "#cc0101" }}>
                  <div>{isSuccess ? "Payment has been processed" : "Payment processing error"}</div>
                  <p style={{ fontSize: "large", color: "#333333" }}>{errorMessage}</p>
                </HeaderContent>
              </Header>
            </Message>
          </div>
        </>
      ) : (
        <div style={{ marginBottom: "50px" }}>
          <br></br>
          <Grid columns="equal" reversed="vertically stackable">
            <GridColumn></GridColumn>
            <GridColumn
              width={8}
              style={{
                background: "#ffffff",
                paddingTop: "0px",
                paddingLeft: "0px",
                paddingRight: "0px",
                borderRadius: "15px",
              }}
            >
              <div
                style={{
                  background: "#182a53",
                  borderTopLeftRadius: "15px",
                  borderTopRightRadius: "15px",
                }}
              >
                <h1 style={{ textAlign: "center", color: "white" }}>Informacion del Pago</h1>
              </div>
              <br></br>

              {experienceVoucher && (
                <>
                  <PaymentInfo experienceVoucher={experienceVoucher}></PaymentInfo>
                  <Container className="w-100">
                    <PaymentMethodSelector
                      bankList={experienceVoucher?.voucherConfig?.bankList}
                    ></PaymentMethodSelector>
                  </Container>
                </>
              )}
            </GridColumn>
            <GridColumn></GridColumn>
          </Grid>
        </div>
      )}
    </div>
  );
};

export default MonnetVoucherHome;
